import { ApisauceInstance } from 'apisauce';
import firebase from 'firebase/compat/app';
import moment from 'moment';

import shareReportOrThrow from '../Report/shareReportOrThrow';

// Headless (scheduled) export for a dashboard. Mirrors the headless branch of
// Report's exportReport: the renderer has already produced + uploaded the PDF
// and inline preview image, so this uploads the per-gadget CSVs under the same
// exportId and shares the PDF + preview + CSVs via /v1/share/report.
//
// contentType: 'dashboard' tells the (cross-repo) email service to use
// dashboard wording and the dashboard deep-link.
const exportDashboardHeadless = async ({
  dashboard,
  accountId,
  api,
  senderId,
  recipientUserIds,
  notes,
  savedFilterId,
  gridExportCallbacks,
  headless,
}: {
  dashboard: DashboardType;
  accountId: string;
  api: ApisauceInstance;
  senderId: string;
  recipientUserIds: string[];
  notes: string;
  savedFilterId: string;
  gridExportCallbacks: GridExportCallback[];
  headless: { pdfPath: string; reportImagePath?: string; exportId: string };
}): Promise<void> => {
  const { exportId } = headless;
  const storage = firebase.storage().ref();
  const pathBase = `accounts/${accountId}/exports/${exportId}`;

  // Upload per-gadget CSV exports. Two gadgets can share a name, which yields an
  // identical CSV filename; a unique suffix stops the second upload overwriting
  // the first (matches the report export's dedupe).
  const excelPaths: string[] = [];
  const usedFileNames = new Set<string>();
  for (let i = 0; i < gridExportCallbacks.length; i++) {
    const fileData = await gridExportCallbacks[i].callback();
    let fileName = fileData.fileName;
    if (usedFileNames.has(fileName)) {
      let suffix = 2;
      while (usedFileNames.has(`${fileData.fileName} (${suffix})`)) {
        suffix += 1;
      }
      fileName = `${fileData.fileName} (${suffix})`;
    }
    usedFileNames.add(fileName);
    const filePath = `${pathBase}/${fileName}.csv`;
    const response = await storage
      .child(filePath)
      .putString(fileData.content, undefined, { contentType: 'text/csv' });
    excelPaths.push(response.metadata.fullPath);
  }

  const dashboardLink = `${window.location.origin}/dashboards/${dashboard.id}?savedFilterId=${savedFilterId}`;

  const exportData: ReportExportBody = {
    id: exportId,
    name: `${dashboard.name} - ${moment.utc().format('Do MMM YYYY')}`,
    senderId,
    // The email embeds reportPath (the inline preview image) in the body.
    reportPath: headless.reportImagePath ?? '',
    pdfPath: headless.pdfPath,
    excelPaths,
    // Dashboards have no single date range; variable-filter selections are
    // captured in the snapshot and reflected in the rendered content itself.
    dateRange: '',
    filtering: 'None',
    reportLink: dashboardLink,
    reportName: dashboard.name,
    userIds: recipientUserIds,
    notes: notes === '' ? 'None' : notes,
    isScheduled: true,
    contentType: 'dashboard',
  };

  // A non-OK share throws on the headless path so the renderer releases its
  // emailDelivered claim and Cloud Tasks retries (see shareReportOrThrow).
  await shareReportOrThrow({ exportData, api, headless: true });
};

export default exportDashboardHeadless;
